import { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { X } from 'lucide-react';
import { wordSearchService, type WordSearchData } from '../../services/wordSearchService';
import WordSearchItem from './WordSearchItem';

interface WordInputProps {
  onAddWord: (word: string, searchData?: import('../../services/wordSearchService').WordSearchData) => void;
  disabled?: boolean;
}

function WordInput({ onAddWord, disabled = false }: WordInputProps) {
  const [inputValue, setInputValue] = useState<string>('');
  const [searchResult, setSearchResult] = useState<WordSearchData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced word search
  useEffect(() => {
    const trimmedValue = inputValue.trim();
    
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    // Clear results if user cleared the input
    if (!trimmedValue) {
      setSearchResult(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    // Don't search for very short queries
    if (trimmedValue.length < 2) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    debounceTimerRef.current = setTimeout(async () => {
      try {
        const result = await wordSearchService.searchWord(trimmedValue);
        if (result.data && result.data.length > 0) {
          setSearchResult(result);
          setError(null);
        } else {
          setError('Word not found');
          setSearchResult(null);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to search word';
        setError(errorMessage);
        setSearchResult(null);
      } finally {
        setIsLoading(false);
      }
    }, 500); // 500ms debounce

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [inputValue]);

  const handleAdd = () => {
    const trimmedWord = inputValue.trim();
    if (trimmedWord && !disabled) {
      onAddWord(trimmedWord, searchResult || undefined);
      setInputValue('');
      setSearchResult(null);
      setError(null);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && canAddWord && !disabled) {
      handleAdd();
    }
  };

  const hasResults = searchResult !== null && inputValue.trim().length >= 2;
  const canAddWord = hasResults && !isLoading && !error && inputValue.trim().length >= 2;

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2">
        <div className="flex-1 space-y-3">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Input
                id="word-input"
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder='Type a word to learn'
                className="pr-10"
              />
              {inputValue && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setInputValue('');
                    setSearchResult(null);
                    setError(null);
                  }}
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0 hover:bg-muted-foreground/20"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            <Button
              onClick={handleAdd}
              disabled={disabled || !canAddWord}
              className="shrink-0"
            >
              {isLoading ? 'Searching...' : hasResults ? 'Add' : 'Type to search'}
            </Button>
          </div>
          
          {/* Search Result */}
          {hasResults && (
            <WordSearchItem searchData={searchResult} />
          )}

          {/* Loading State */}
          {inputValue.trim().length >= 2 && isLoading && (
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                  <p className="text-sm text-muted-foreground">Searching...</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Error State */}
          {error && (
            <Card className="border-red-200 bg-red-50 dark:bg-red-950/20">
              <CardContent className="pt-4 pb-4">
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

export default WordInput;
